/**
 * EditableText (web) — Renders text. If the current user is the owner,
 * shows a small pencil affordance on hover. Clicking it opens an inline
 * editor (popover) to update the brand text.
 *
 * Non-owners (including admins, customers, drivers, anon) see plain text
 * with zero affordance. The owner can edit it from any page at any time.
 */

import React, { useState, useRef, useEffect } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";

export default function EditableText({
  brandKey,
  defaultValue,
  as = "span",
  className = "",
  multiline = false,
  prefix = "",
  suffix = "",
  iconClassName = "",
  testId,
}) {
  const { user } = useAuth();
  const { get, updateText } = useBranding();

  const value = get(brandKey, defaultValue);
  const isOwner = !!user?.is_owner;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch {
        /* noop */
      }
    }
  }, [editing]);

  const openEditor = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setDraft(get(brandKey, defaultValue));
    setError("");
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setError("");
  };
  const save = async () => {
    setSaving(true);
    const res = await updateText(brandKey, draft);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed");
      return;
    }
    setEditing(false);
  };

  const Tag = as;
  const display = (
    <Tag className={className} data-testid={testId}>
      {prefix}
      {value}
      {suffix}
    </Tag>
  );

  if (!isOwner) return display;

  return (
    <span className="relative inline-flex items-center group" data-testid={testId ? `${testId}-wrap` : undefined}>
      {display}
      <button
        type="button"
        onClick={openEditor}
        className={`opacity-0 group-hover:opacity-100 transition-opacity ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF3008] text-white shadow-md hover:scale-110 ${iconClassName}`}
        aria-label={`Edit ${brandKey}`}
        data-testid={`edit-${brandKey}-btn`}
      >
        <Pencil size={11} strokeWidth={2.5} />
      </button>

      {editing && (
        <span
          className="absolute z-50 left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[280px]"
          onClick={(e) => e.stopPropagation()}
          data-testid={`edit-${brandKey}-popover`}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Owner edit · {brandKey}
          </div>
          {multiline ? (
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#FF3008] outline-none"
              data-testid={`edit-${brandKey}-input`}
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                else if (e.key === "Escape") cancel();
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#FF3008] outline-none"
              data-testid={`edit-${brandKey}-input`}
            />
          )}
          {error && (
            <div className="text-[11px] text-red-600 mt-1">{error}</div>
          )}
          <div className="flex justify-end gap-1 mt-2">
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
              data-testid={`edit-${brandKey}-cancel`}
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FF3008] hover:bg-[#e02a06] text-white"
              data-testid={`edit-${brandKey}-save`}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            </button>
          </div>
        </span>
      )}
    </span>
  );
}
